import {League} from "../model/league";

/**
 * One display name per team, whichever kind of row it came from.
 *
 * The scoresheet list used to read "BEA 8 - 4 DVH" for a finished game and
 * "Beavers Chomutov (CZE) vs DVH Amstelveen (NED)" for a scheduled one, in the
 * same list. The cause is not a missing name: WBSC live data puts the IOC code
 * in `fullName` as well, so a played game's team arrives as
 * `{shortName: 'BEA', fullName: 'BEA'}` and a plain `fullName || shortName`
 * chain takes it and never consults the schedule row, which had the real name
 * all along.
 *
 * So "has a fullName" is not the same as "has a real name": a candidate equal to
 * the team's own abbreviation is skipped, and the short code is used only when
 * nothing better exists anywhere.
 */
export function resolveTeamName(
  league: League | undefined,
  teamId: unknown,
  /** Names in preference order, best first. */
  candidates: (string | undefined)[],
  shortName: string | undefined,
  fallback: string,
): string {
  const short = (shortName ?? '').trim();

  for (const candidate of withConfiguredName(league, teamId, candidates)) {
    const name = (candidate ?? '').trim();
    if (!name) continue;
    if (short && name.toLowerCase() === short.toLowerCase()) continue;
    return name;
  }
  return short || fallback;
}

/**
 * The league's own `teams[]` entry, spliced in after the caller's first choice.
 *
 * The schedule row stays the first choice because those are the rows that
 * already render correctly, and matching them is the whole point. The league
 * config comes next: it is the only source for a game with no schedule row at
 * all, and it is the same name broadcasts display, so the two agree.
 */
function withConfiguredName(
  league: League | undefined,
  teamId: unknown,
  candidates: (string | undefined)[],
): (string | undefined)[] {
  const all = [...candidates];
  all.splice(1, 0, configuredTeamName(league, teamId));
  return all;
}

/**
 * `league.teams[].id` is a string, while a schedule row's team id can arrive as
 * a number — compared as strings so a real match is not missed.
 */
export function configuredTeamName(league: League | undefined, teamId: unknown): string | undefined {
  if (teamId == null || teamId === '') return undefined;
  const wanted = String(teamId);
  return league?.teams?.find(t => String(t.id) === wanted)?.fullName;
}
